import React, { useState } from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { useAuth } from '../../contexts/AuthContext'

const FormContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
  padding: ${theme.spacing.xl};
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
`

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  padding-bottom: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};
  border-bottom: 1px solid ${({ theme }) => theme.colors.textMuted};
`

const LogoText = styled.h1`
  font-size: ${theme.typography.xl};
  font-family: 'Inter', sans-serif;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  width: 100%;
  text-align: center;
`

const Logo = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: contain;
  margin: 0 auto;
`

const FormTitle = styled.h1`
  font-size: ${theme.typography.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin-bottom: ${theme.spacing.md};
`

const FormDescription = styled.p`
  font-size: ${theme.typography.md};
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
  line-height: 1.5;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`

const FormLabel = styled.label`
  font-size: ${theme.typography.md};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`

const FormInput = styled.input`
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }
`

const PasswordRequirements = styled.div`
  font-size: ${theme.typography.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${theme.spacing.xs};
  
  ul {
    margin: ${theme.spacing.xs} 0;
    padding-left: ${theme.spacing.md};
  }
  
  li {
    margin: ${theme.spacing.xs} 0;
  }
`

const SubmitButton = styled.button<{ $loading?: boolean }>`
  background-color: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: #000000;
  font-size: ${theme.typography.md};
  font-weight: 600;
  cursor: ${(props) => (props.$loading ? 'not-allowed' : 'pointer')};
  transition: ${theme.transitions.default};
  margin-top: ${theme.spacing.sm};
  
  &:hover {
    background-color: ${({ theme, $loading }) => ($loading ? theme.colors.primary : theme.colors.primaryDark)};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const ErrorMessage = styled.div`
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid #ff6b6b;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.sm};
  color: #ff6b6b;
  font-size: ${theme.typography.md};
  margin-top: ${theme.spacing.sm};
`

const SuccessMessage = styled.div`
  background-color: rgba(151, 251, 87, 0.1);
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${theme.typography.md};
  margin-top: ${theme.spacing.sm};
`

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  text-decoration: underline;
  cursor: pointer;
  font-size: ${theme.typography.md};
  margin-top: ${theme.spacing.md};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  align-items: center;
  margin-top: ${theme.spacing.md};
`

export const ChangePasswordForm: React.FC = () => {
  const { changePassword, clearPasswordRecovery } = useAuth()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const validatePasswords = () => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters long'
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match'
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    const validationError = validatePasswords()
    if (validationError) {
      setError(validationError)
      return
    }

    setLoading(true)

    try {
      const { error } = await changePassword(password)
      if (error) {
        setError(error.message)
      } else {
        setSuccess('Password changed successfully! You will be redirected shortly.')
        // Clear the form
        setPassword('')
        setConfirmPassword('')

        // Redirect to main app after a short delay
        setTimeout(() => {
          clearPasswordRecovery()
        }, 2000)
      }
    } catch (err) {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    clearPasswordRecovery()
  }

  return (
    <FormContainer>
      <LogoContainer>
        <Logo src="/logo_512.png" alt="supacoach Logo" />
        <LogoText>supacoach</LogoText>
      </LogoContainer>

      <FormTitle>Set New Password</FormTitle>
      <FormDescription>Please enter your new password. Make sure it's secure and memorable.</FormDescription>

      <Form onSubmit={handleSubmit}>
        <FormField>
          <FormLabel>New Password</FormLabel>
          <FormInput
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your new password"
            required
            disabled={loading || success !== null}
            minLength={6}
          />
          <PasswordRequirements>
            <ul>
              <li>At least 6 characters long</li>
              <li>Should be unique and memorable</li>
            </ul>
          </PasswordRequirements>
        </FormField>

        <FormField>
          <FormLabel>Confirm New Password</FormLabel>
          <FormInput
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your new password"
            required
            disabled={loading || success !== null}
            minLength={6}
          />
        </FormField>

        <SubmitButton type="submit" $loading={loading} disabled={loading || success !== null}>
          {loading ? 'Updating Password...' : 'Update Password'}
        </SubmitButton>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <FormActions>
          <LinkButton type="button" onClick={handleCancel} disabled={loading}>
            Cancel
          </LinkButton>
        </FormActions>
      </Form>
    </FormContainer>
  )
}
